/**
 * THE CONTACT BLOCK — the one place on this page where absence is the design.
 *
 * It replaces the prototype's "Still stuck? → Open Chats" panel (proto:3080-3086). Chats is PRO-106
 * and support tickets are PRO-111, and LOCKED D37 defers both: "RULED 2026-08-21: both deferred —
 * e-mail is enough for v1" (`docs/design/00-scope-v1.md`, deferred column at :404). The panel
 * keeps its job — escalation — and changes its mechanism to the two addresses from
 * `getPlatformContacts`.
 *
 * ABSENT IS SHOWN AS ABSENT, AND THIS IS A CONTRACT REQUIREMENT, NOT A PREFERENCE.
 * `authors-api.yaml`: "Either address may be absent, and absent is reported rather than
 * papered over … a client shows what it is given and never a placeholder address, because a support
 * page that prints a wrong address is worse than one that prints none." The schema repeats it and
 * the table seeds the ROW without the ADDRESSES — `INSERT INTO platform_settings (only_row,
 * updated_at) VALUES (true, …)` — with both columns NULL until an operator sets them at deployment,
 * "a named M0 deployment step, not a migration" (`04-schema.md`). A1's acceptance script
 * step 7 asserts exactly the both-absent state on a fresh box (`12-build-plan.md`).
 *
 * SO THERE IS NO FALLBACK ADDRESS ANYWHERE IN THIS FILE, and there must never be one.
 *
 * BRANCH ON THE VALUE, NEVER ON KEY PRESENCE. Both keys are `required` in the schema and both
 * values are `string | null`, so `'supportEmail' in contacts` is always true and tells you nothing.
 *
 * WHAT IS NOT HERE. `maintenance_banner` sits on the same `platform_settings` row and is
 * deliberately not exposed to the cabinet: "it was not part of what was ruled, and a field added
 * because it happened to be adjacent is how scope grows without a decision"
 * (`authors-api.yaml`). Do not add it.
 */
import { Anchor, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from '../../../i18n';
import type { PlatformContacts } from '../api/contract';
import { isForbidden, isRetryable, isUnauthenticated } from '../api/errors';
import type { AsyncResource } from '../hooks/use-async-resource';
import { AbsenceBlock, FailureBlock, LoadingBlock } from './StateBlocks';
import classes from '../support.module.css';

interface AddressRowProps {
  readonly label: string;
  readonly gloss: string;
  readonly address: string;
}

function AddressRow({ label, gloss, address }: AddressRowProps) {
  return (
    <div>
      <div className={classes.contactRow}>
        <Text size="sm" fw={700}>
          {label}
        </Text>
        {/* `mailto:` is a top-level navigation, not a subresource load, so the cabinet's
            `default-src 'self'` CSP baseline (adr/0007:13) does not block it. No CSP change is
            owed for this page. */}
        <Anchor size="sm" href={`mailto:${address}`}>
          {address}
        </Anchor>
      </div>
      <Text size="xs" c="dimmed">
        {gloss}
      </Text>
    </div>
  );
}

export interface ContactBlockProps {
  readonly contacts: AsyncResource<PlatformContacts>;
  /** Called on a 401 so the shell can re-authenticate. A 401 is never "support is broken". */
  readonly onSessionExpired: () => void;
}

export function ContactBlock({ contacts, onSessionExpired }: ContactBlockProps) {
  const { t } = useTranslation('support');
  if (contacts.status === 'loading') {
    return (
      <section className={classes.contactPanel} aria-labelledby="support-contact-heading">
        <Title id="support-contact-heading" order={2} size="h4" mb="sm">
          {t('page.contactHeading')}
        </Title>
        <LoadingBlock label={t('page.contactLoading')} lines={2} />
      </section>
    );
  }

  if (contacts.status === 'failed' && contacts.error !== null) {
    const error = contacts.error;

    if (isUnauthenticated(error)) {
      return (
        <section className={classes.contactPanel} aria-labelledby="support-contact-heading">
          <Title id="support-contact-heading" order={2} size="h4" mb="sm">
            {t('page.contactHeading')}
          </Title>
          <FailureBlock
            title={t('page.sessionExpiredTitle')}
            body={t('page.sessionExpiredBody')}
            onRetry={onSessionExpired}
            retryLabel={t('page.signInAgain')}
          />
        </section>
      );
    }

    // 403. The corpus names no error code on this refusal — the response is a bare `$ref` to
    // `Problem403` — so nothing here switches on one and the copy asserts no reason. The page
    // around this block stays useful: the guides and the terms are not 403-gated.
    if (isForbidden(error)) {
      return (
        <section
          className={`${classes.contactPanel} ${classes.contactPanelEmpty}`}
          aria-labelledby="support-contact-heading"
        >
          <Title id="support-contact-heading" order={2} size="h4" mb="sm">
            {t('page.contactHeading')}
          </Title>
          <AbsenceBlock
            title={t('page.contactForbiddenTitle')}
            body={t('page.contactForbiddenBody')}
          />
        </section>
      );
    }

    return (
      <section className={classes.contactPanel} aria-labelledby="support-contact-heading">
        <Title id="support-contact-heading" order={2} size="h4" mb="sm">
          {t('page.contactHeading')}
        </Title>
        <FailureBlock
          title={t('page.contactFailedTitle')}
          body={t('page.contactFailedBody')}
          {...(isRetryable(error)
            ? {
                onRetry: contacts.reload,
                retryLabel: t('page.contactRetry'),
                retrying: contacts.reloading,
              }
            : {})}
        />
      </section>
    );
  }

  const data = contacts.data;
  if (data === null) {
    return null;
  }

  const support = data.supportEmail;
  const editorial = data.editorialEmail;
  const bothAbsent = support === null && editorial === null;
  const oneAbsent = !bothAbsent && (support === null || editorial === null);

  return (
    <section
      className={`${classes.contactPanel} ${bothAbsent ? classes.contactPanelEmpty : ''}`}
      aria-labelledby="support-contact-heading"
    >
      <Title id="support-contact-heading" order={2} size="h4" mb="sm">
        {t('page.contactHeading')}
      </Title>

      {bothAbsent ? (
        <AbsenceBlock title={t('page.contactNoneTitle')} body={t('page.contactNoneBody')} />
      ) : (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('page.contactIntro')}
          </Text>
          {support === null ? null : (
            <AddressRow
              label={t('page.contactSupportLabel')}
              gloss={t('page.contactSupportGloss')}
              address={support}
            />
          )}
          {editorial === null ? null : (
            <AddressRow
              label={t('page.contactEditorialLabel')}
              gloss={t('page.contactEditorialGloss')}
              address={editorial}
            />
          )}
          {oneAbsent ? (
            // Say which half is missing rather than letting a one-address panel look complete.
            <Text size="xs" c="dimmed" fs="italic">
              {t('page.contactOneMissingSuffix')}
            </Text>
          ) : null}
        </Stack>
      )}
    </section>
  );
}
